import WarningIcon from "@material-ui/icons/Warning";
import ErrorIcon from "@material-ui/icons/Error";
import InfoIcon from "@material-ui/icons/Info";
import { alpha, makeStyles, Theme } from "@material-ui/core";
import { ComponentType, createContext } from "react";
import GridLayout from "react-grid-layout";
import {
  MaintenanceDashboardApiFactory,
  MaintenanceDashboardConfigurationDto,
} from "../../../common/data/service-proxies";
import { AxiosInstance } from "axios";

export type WidgetIconType = "info" | "warning" | "error";

export interface WidgetProps {
  className?: string;
  title: string;
  icon: WidgetIconType;
  configuration?: any;
  onConfigurationSaved?: (value: any) => void;
}

export const getWidgetIcon = (icon: WidgetIconType) => {
  switch (icon) {
    case "warning":
      return <WarningIcon />;
    case "error":
      return <ErrorIcon />;
    case "info":
      return <InfoIcon />;
  }
};

export const getIconColor = (icon: WidgetIconType, theme: Theme) => {
  switch (icon) {
    case "warning":
      return theme.palette.warning.main;
    case "error":
      return theme.palette.error.main;
    case "info":
      return theme.palette.info.main;
  }
};

export const useTableHeaderStyles = makeStyles((theme) => ({
  tableHead: {
    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    position: "sticky",
    top: "-1px",
    zIndex: 2,
  },
  clickable: {
    cursor: "pointer",
  },
  row: {
    "&:hover": {
      background: alpha(
        theme.palette.action.hover,
        theme.palette.action.hoverOpacity
      ),
    },
  },
}));

export const useButtonStyles = makeStyles(() => ({
  label: {
    justifyContent: "flex-start",
  },
}));

export type SelectedVehiclesFilter =
  | "WITH_DIAGNOSTIC_ERRORS"
  | "WITH_UNRESOLVED_DVIR_DEFECTS"
  | "WITH_ELD_ALERTS"
  | "WITH_TPMS_ALERTS"
  | "WITH_MAJOR_UNRESOLVED_DVIR_DEFECTS"
  | "WITH_MINOR_UNRESOLVED_DVIR_DEFECTS"
  | "WITH_OVERDUE_SERVICES"
  | "WITH_UPCOMING_SERVICES"
  | "WITH_CONDITION_GOOD"
  | "WITH_CONDITION_SATISFACTORY"
  | "WITH_CONDITION_CRITICAL";

export const SelectedVehiclesFilterContext = createContext<
  SelectedVehiclesFilterAccessor | undefined
>(undefined);

export interface SelectedVehiclesFilterAccessor {
  selectedFilter?: SelectedVehiclesFilter;
  setSelectedFilter: (value?: SelectedVehiclesFilter) => void;
}

export const getDefaultLayoutProps = () => {
  return {
    className: "layout",
    cols: { xl: 12, lg: 12, md: 6, sm: 6, xs: 4, xxs: 2 },
    rowHeight: 192,
  };
};

export interface GridLayoutWidget<TProps extends WidgetProps = WidgetProps> {
  i: string;
  Widget: ComponentType<TProps>;
  icon: WidgetIconType;
  title: string;
}

export const setEditable = (
  { widget, payload, ...previous }: GridLayout.Layouts,
  editable: boolean
): GridLayout.Layouts => {
  const layoutNames = Object.getOwnPropertyNames(previous);
  return layoutNames.reduce((prev, current) => {
    const newLayout = { ...prev };
    newLayout[current] = [
      ...newLayout[current].map((x) => ({ ...x, static: !editable })),
    ];
    return newLayout;
  }, previous);
};

type IMaintenanceDashboardSaveLayoutContext =
  | ((configuration: MaintenanceDashboardConfigurationDto) => void)
  | undefined;

export const MaintenanceDashboardSaveLayoutContext = createContext<
  IMaintenanceDashboardSaveLayoutContext
>(undefined);

export const saveMaintenanceDashboardConfiguration = async (
  axiosInstance: AxiosInstance,
  payload: MaintenanceDashboardConfigurationDto
): Promise<MaintenanceDashboardConfigurationDto> => {
  const configuration = await MaintenanceDashboardApiFactory(
    undefined,
    axiosInstance.defaults.baseURL,
    axiosInstance
  ).apiAppMaintenancedashboardSavemaintenancedashboardconfigurationPost(
    payload
  );
  return configuration.data;
};
